// Dependency imports
import { useAppSelector } from 'hooks';
import { AppDispatch, RootState } from 'store';

// Local imports
import { Button } from 'components';
import { useConfig } from 'hooks/config';
import { ConfigSteps } from 'types';
import AnnotationPage from './annotationCanvas/AnnotationPage';

// Style imports
import {
  useGetAnnotationValidation,
  useListConfigurations,
  useUpdateFrameData
} from 'api/sdk';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SearchSelect from 'components/SearchSelect';
import { setCurrentCameraId } from 'store/config/configSlice';
import { setFrameIndex } from 'store/annotations/annotationSlice';
import { useEffect, useState } from 'react';
import { useSnack } from 'plugins/snack';
import { match } from 'assert';

const PickAndPlace = () => {
  const navigate = useNavigate();
  const { data: configs } = useListConfigurations();
  const dispatch = useDispatch();
  const [filteredcameras, setCameras] = useState<any>([]);
  const snack = useSnack();
  const videoData = useSelector(
    (data: any) => data?.configuration?.filteredVideo
  );
  const cameras = useSelector((state: RootState) => state.configV2.cameras);
  const frameIndex = useSelector(
    (state: RootState) => state.annotation.frameIndex
  );

  const annotationData: any = useSelector(
    (state: RootState) => state.annotation.annotationData
  );
  const currentConfigId: string | undefined = useSelector(
    (state: RootState) => state.configuration.configuration
  );

  const currentCameraId: any = useSelector(
    (state: RootState) => state.configV2.currentCameraId
  );

  const selectedCameras = filteredcameras
    .map((camera: any) => camera.id)
    .join(',');

  const { refetch } = useGetAnnotationValidation(
    currentConfigId,
    selectedCameras
  );

  const { mutateAsync: updateFrameData, status } = useUpdateFrameData();

  useEffect(() => {
    if (cameras && videoData) {
      const matchedCameras = cameras?.filter((camData: any) =>
        videoData?.includes(camData?.id)
      );
      dispatch(setCurrentCameraId(matchedCameras[0]['id']));
      setCameras(matchedCameras);
    }
  }, [cameras, videoData]);

  return (
    <div className="w-full p-4 flex flex-col gap-4">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.1vw] font-[Proxima-Nova-Bold]">Activity Flow</h2>
        <div className="flex items-center justify-end gap-2">
          <SearchSelect
            data={[
              ...(configs?.data.map((config: any) => ({
                label: config.name,
                value: config.id
              })) || [])
            ]}
            disabled
            width="10vw"
            label="Select Configuration"
            selectValue={currentConfigId}
            placeholder="Configuration"
          />
          <SearchSelect
            label="Select Video"
            data={
              filteredcameras
                ?.filter((data: any) => data.video_url)
                .map((camera: any, index: number) => ({
                  label: camera.name,
                  value: camera.id
                })) || []
            }
            selectValue={currentCameraId}
            onChange={(value: any) => {
              dispatch(setFrameIndex(1));
              console.log('value of camera', value);
              dispatch(setCurrentCameraId(value));
            }}
            width="10vw"
            placeholder="Video"
          />
        </div>
      </div>
      <AnnotationPage />

      <div className="w-full flex items-center justify-center gap-4 mt-4">
        <Button
          variant="outlined"
          style={{
            background: 'white',
            border: '1px solid #102633',
            color: '#102633'
          }}
          onClick={() => navigate(`/train/videos`)}
        >
          Back
        </Button>
        <Button
          onClick={() => {
            console.log('frame data being updated on save', {
              configId: currentConfigId || '',
              cameraId: currentCameraId || '',
              frameIdx: frameIndex,
              data: {
                bounding_box: {
                  frame_data: annotationData
                }
              }
            });
            updateFrameData({
              configId: currentConfigId || '',
              cameraId: currentCameraId || '',
              frameIdx: frameIndex,
              data: {
                bounding_box: {
                  frame_data: annotationData
                }
              }
            }).then(response => {
              if (response.status === 200) {
                console.log('response', response);
                snack({
                  message: response?.data?.message,
                  severity: 'success'
                });
              } else {
                snack({
                  message: 'Something went wrong',
                  severity: 'error'
                });
              }
            });
          }}
        >
          Save
        </Button>
        <Button
          onClick={() => {
            refetch().then(response => {
              console.log('response from the data', response?.data?.data);
              if (response?.data?.data?.is_valid) {
                navigate('/train/pick_&_place_annotation/activity_mapping');
              } else {
                Swal.fire({
                  title: 'Warning!',
                  text: `Annotate atleast 200 images from the video selection for better accuracy\n (Total annotated: ${response?.data?.data?.total_annotations})`,
                  allowOutsideClick: false,
                  confirmButtonText: 'Proceed',
                  showCancelButton: true,
                  confirmButtonColor: '#E33030',
                  cancelButtonColor: '#102633',
                  cancelButtonText: 'Cancel!',
                  focusCancel: true,
                  customClass: {
                    title: 'swal2-title',
                    container: 'swal2-html-container',
                    actions: 'swal2-actions'
                  }
                }).then(async result => {
                  if (result.isConfirmed) {
                    navigate('/train/pick_&_place_annotation/activity_mapping');
                  }
                });
              }
            });
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default PickAndPlace;
